import { supabase } from './supabase';

export interface LogUsuario {
  id: string;
  usuario_id: string;
  usuario_nome: string;
  usuario_login: string;
  acao: string;
  modulo: string;
  descricao: string;
  dados_anteriores: any;
  dados_novos: any;
  ip_address: string | null;
  user_agent: string | null;
  data_acao: string;
  filial_atual: string | null;
}

class LogsUsuariosService {
  // Registrar log de ação do usuário
  async registrarLog(
    usuario: any,
    acao: string,
    modulo: string,
    descricao: string,
    dadosAnteriores?: any,
    dadosNovos?: any,
    filialAtual?: string
  ) {
    try {
      const dadosLog = {
        usuario_id: usuario.id,
        usuario_nome: usuario.nome,
        usuario_login: usuario.login || usuario.email,
        acao,
        modulo,
        descricao,
        dados_anteriores: dadosAnteriores || null,
        dados_novos: dadosNovos || null,
        ip_address: await this.obterIP(),
        user_agent: navigator.userAgent,
        filial_atual: filialAtual || null
      };

      const { error } = await supabase
        .from('logs_usuarios')
        .insert(dadosLog);

      if (error) {
        console.error('Erro ao registrar log:', error);
      }

      return { success: !error };
    } catch (error) {
      console.error('Erro ao registrar log:', error);
      return { success: false };
    }
  }

  // Buscar logs recentes
  async buscarLogsRecentes(limite: number = 50): Promise<LogUsuario[]> {
    try {
      const { data, error } = await supabase
        .from('logs_usuarios')
        .select('*')
        .order('data_acao', { ascending: false })
        .limit(limite);

      if (error) {
        console.error('Erro ao buscar logs:', error);
        return [];
      }

      return data || [];
    } catch (error) {
      console.error('Erro ao buscar logs:', error);
      return [];
    }
  }

  // Buscar logs por usuário
  async buscarLogsPorUsuario(usuarioId: string, limite: number = 50): Promise<LogUsuario[]> {
    try {
      const { data, error } = await supabase
        .from('logs_usuarios')
        .select('*')
        .eq('usuario_id', usuarioId)
        .order('data_acao', { ascending: false })
        .limit(limite);

      if (error) {
        console.error('Erro ao buscar logs do usuário:', error);
        return [];
      }

      return data || [];
    } catch (error) {
      console.error('Erro ao buscar logs do usuário:', error);
      return [];
    }
  }

  // Buscar logs por módulo
  async buscarLogsPorModulo(modulo: string, limite: number = 50): Promise<LogUsuario[]> {
    try {
      const { data, error } = await supabase
        .from('logs_usuarios')
        .select('*')
        .eq('modulo', modulo)
        .order('data_acao', { ascending: false })
        .limit(limite);

      if (error) {
        console.error('Erro ao buscar logs do módulo:', error);
        return [];
      }

      return data || [];
    } catch (error) {
      console.error('Erro ao buscar logs do módulo:', error);
      return [];
    }
  }

  // Buscar logs por período
  async buscarLogsPorPeriodo(dataInicio: Date, dataFim: Date, limite: number = 100): Promise<LogUsuario[]> {
    try {
      const { data, error } = await supabase
        .from('logs_usuarios')
        .select('*')
        .gte('data_acao', dataInicio.toISOString())
        .lte('data_acao', dataFim.toISOString())
        .order('data_acao', { ascending: false })
        .limit(limite);

      if (error) {
        console.error('Erro ao buscar logs por período:', error);
        return [];
      }

      return data || [];
    } catch (error) {
      console.error('Erro ao buscar logs por período:', error);
      return [];
    }
  }

  // Limpar logs antigos
  async limparLogsAntigos(): Promise<{ success: boolean }> {
    try {
      const { error } = await supabase
        .rpc('limpar_logs_antigos');

      if (error) {
        console.error('Erro ao limpar logs antigos:', error);
      }

      return { success: !error };
    } catch (error) {
      console.error('Erro ao limpar logs antigos:', error);
      return { success: false };
    }
  }

  // Obter IP do cliente
  private async obterIP(): Promise<string> {
    try {
      const response = await fetch('https://api.ipify.org?format=json');
      const data = await response.json();
      return data.ip || '0.0.0.0';
    } catch (error) {
      return '0.0.0.0';
    }
  }

  // Métodos específicos para ações comuns
  async registrarLogin(usuario: any, filialAtual?: string) {
    return this.registrarLog(
      usuario,
      'LOGIN',
      'AUTENTICACAO',
      `Usuário ${usuario.nome} fez login no sistema`,
      null,
      null,
      filialAtual
    );
  }

  async registrarLogout(usuario: any, filialAtual?: string) {
    return this.registrarLog(
      usuario,
      'LOGOUT',
      'AUTENTICACAO',
      `Usuário ${usuario.nome} fez logout do sistema`,
      null,
      null,
      filialAtual
    );
  }

  async registrarCriacao(usuario: any, modulo: string, descricao: string, dadosCriados: any, filialAtual?: string) {
    return this.registrarLog(
      usuario,
      'CREATE',
      modulo,
      descricao,
      null,
      dadosCriados,
      filialAtual
    );
  }

  async registrarAtualizacao(usuario: any, modulo: string, descricao: string, dadosAnteriores: any, dadosNovos: any, filialAtual?: string) {
    return this.registrarLog(
      usuario,
      'UPDATE',
      modulo,
      descricao,
      dadosAnteriores,
      dadosNovos,
      filialAtual
    );
  }

  async registrarExclusao(usuario: any, modulo: string, descricao: string, dadosExcluidos: any, filialAtual?: string) {
    return this.registrarLog(
      usuario,
      'DELETE',
      modulo,
      descricao,
      dadosExcluidos,
      null,
      filialAtual
    );
  }

  async registrarVisualizacao(usuario: any, modulo: string, descricao: string, filialAtual?: string) {
    return this.registrarLog(
      usuario,
      'VIEW',
      modulo,
      descricao,
      null,
      null,
      filialAtual
    );
  }
}

export default new LogsUsuariosService();
